import { __awaiter } from "tslib";
import { tryAbandonedPart } from "./device-transport";
const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();
function log(message) {
    // eslint-disable-next-line no-console
    console.log(`[WebSerialTransport] ${message}`);
}
// Authentic Arduinos report this productId and are the only tested boards that raise the
// browser `connect` event, which the UI uses to track physical connection independently
// of port state.
const kArduinoUsbProductId = 67;
export class WebSerialTransport {
    constructor() {
        Object.defineProperty(this, "onData", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "onDisconnect", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "deviceFamily", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "deviceInfo", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "port", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        // The port most recently given to or chosen in open(); unlike `port`, it is kept after close()
        // so it can be reopened.
        Object.defineProperty(this, "lastPort", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        // The error behind a "failed" or "stillOpening" result from open().
        Object.defineProperty(this, "lastOpenError", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "writer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        // The read loop's current reader. It holds a lock on `port.readable`, which must be
        // released before the port can be closed.
        Object.defineProperty(this, "activeReader", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "reading", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "READ_TIMEOUT_MS", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 2000
        });
    }
    // Whether this board raises the browser `connect` event (see initWebSerialConnectionEvents). Only
    // authentic Arduinos were observed to raise it. Clone Arduinos raise `disconnect` too.
    get raisesWebSerialConnect() {
        var _a;
        return ((_a = this.deviceInfo) === null || _a === void 0 ? void 0 : _a.usbProductId) === kArduinoUsbProductId;
    }
    hasOpenPort() {
        return this.port != null && this.port.readable != null;
    }
    determineDeviceFamily(info) {
        return info.usbProductId === 516 && info.usbVendorId === 3368 ? "microbit" : "arduino";
    }
    // Opens `port`, or asks the user to choose one when none is given, then resolves
    // deviceFamily/deviceInfo. Resolves "canceled" if the user dismisses the chooser, "timeout" if
    // port.open hasn't settled within options.timeoutMs, "stillOpening" if the browser says the port
    // is already opening, and "failed" for any other error or a port that cannot be opened for writing.
    open(port_1) {
        return __awaiter(this, arguments, void 0, function* (port, options = {}) {
            var _a, _b;
            this.lastOpenError = undefined;
            try {
                this.port = port !== null && port !== void 0 ? port : yield navigator.serial.requestPort();
                this.lastPort = this.port;
                (_a = options.onChosen) === null || _a === void 0 ? void 0 : _a.call(options);
                this.deviceInfo = yield this.port.getInfo();
                this.deviceFamily = this.determineDeviceFamily(this.deviceInfo);
            }
            catch (error) {
                console.error("error requesting port: ", error);
                if ((error === null || error === void 0 ? void 0 : error.name) === "NotFoundError")
                    return "canceled";
                this.lastOpenError = error;
                return "failed";
            }
            const openingPort = this.port;
            const outcome = yield this.openWithLimit(openingPort, options);
            if (outcome.status === "timeout") {
                this.port = null;
                return "timeout";
            }
            if (outcome.status === "failed") {
                console.error("error opening port: ", outcome.error);
                this.lastOpenError = outcome.error;
                return ((_b = outcome.error) === null || _b === void 0 ? void 0 : _b.name) === "InvalidStateError" ? "stillOpening" : "failed";
            }
            if (!this.port.writable) {
                console.error("Port is not writable");
                yield this.closePort();
                return "failed";
            }
            this.writer = this.port.writable.getWriter();
            return "opened";
        });
    }
    // Races port.open against the time limit. When the limit wins, the port is given up on (see
    // abandonOpen) and this resolves at once.
    openWithLimit(port, options) {
        return __awaiter(this, void 0, void 0, function* () {
            const opening = port.open({ baudRate: 9600 });
            const settled = opening.then(() => ({ status: "opened" }), (error) => ({ status: "failed", error }));
            if (options.timeoutMs == null)
                return settled;
            let timeoutId;
            const timedOut = new Promise((resolve) => {
                timeoutId = setTimeout(() => resolve({ status: "timeout" }), options.timeoutMs);
            });
            const outcome = yield Promise.race([settled, timedOut]).finally(() => clearTimeout(timeoutId));
            if (outcome.status === "timeout")
                this.abandonOpen(port, settled, options);
            return outcome;
        });
    }
    // Tries to close and forget a port whose open was given up on, and closes it if that open
    // succeeds later. Nothing here is awaited: a port stuck opening may not close either, and the
    // time limit mustn't wait for it. Close and forget start together, so one that hangs doesn't
    // keep the other from being tried, and each part reports through options.onAbandoned as it
    // finishes.
    abandonOpen(port, settled, options) {
        const report = (info) => {
            var _a;
            try {
                (_a = options.onAbandoned) === null || _a === void 0 ? void 0 : _a.call(options, info);
            }
            catch (e) {
                console.error("Abandoned open report failed", e);
            }
        };
        const forgettable = port;
        tryAbandonedPart("close", () => port.close(), report);
        tryAbandonedPart("forget", typeof forgettable.forget === "function" ? () => forgettable.forget() : undefined, report);
        void settled.then((late) => __awaiter(this, void 0, void 0, function* () {
            if (late.status === "opened") {
                try {
                    yield port.close();
                }
                catch ( /* the port is abandoned either way */_a) { /* the port is abandoned either way */ }
            }
            report({ late: late.status === "opened" ? "opened" : "failed" });
        }));
    }
    write(line) {
        var _a;
        if (this.hasOpenPort()) {
            (_a = this.writer) === null || _a === void 0 ? void 0 : _a.write(textEncoder.encode(`${line}\n`));
        }
        else {
            log("Port closed, skipping write");
        }
    }
    close() {
        return __awaiter(this, void 0, void 0, function* () {
            this.reading = false;
            const reader = this.activeReader;
            this.activeReader = undefined;
            if (reader) {
                try {
                    yield reader.cancel();
                }
                catch ( /* reader may already be released */_a) { /* reader may already be released */ }
                try {
                    reader.releaseLock();
                }
                catch ( /* lock may already be released */_b) { /* lock may already be released */ }
            }
            yield this.closePort();
            this.port = null;
        });
    }
    // Read loop. Delivers decoded chunks to onData; on give-up (reopen fails) closes the port,
    // then fires onDisconnect. Relies on the browser nulling `port.readable` on a real
    // disconnect to drive the reopen check below.
    startReading() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            // Not proactively stopped by the navigator.serial "disconnect" event; it self-terminates
            // within one reopen cycle once the port stops being readable (see the timeout/reopen logic below).
            this.reading = true;
            while (this.reading && this.port) {
                if (!this.port.readable) {
                    log("Port not readable, attempting to reopen...");
                    const reopened = yield this.reopenPort();
                    if (!reopened || !this.port.readable) {
                        console.error("Failed to reopen port, stopping stream handler");
                        break;
                    }
                }
                const streamReader = this.port.readable.getReader();
                this.activeReader = streamReader;
                try {
                    while (this.reading && this.port.readable) {
                        const { value, done, timedOut } = yield this.readWithTimeout(streamReader);
                        if (timedOut) {
                            log("Read timed out, closing and reopening port...");
                            yield this.closePort(streamReader);
                            break;
                        }
                        if (done)
                            break;
                        (_a = this.onData) === null || _a === void 0 ? void 0 : _a.call(this, value);
                    }
                }
                catch (error) {
                    console.error(error);
                }
                finally {
                    // The streamReader might have already been released by closePort().
                    // If it hasn't, we need to release it here so we can get a new reader later.
                    // It is idempotent so it is safe to call a second time.
                    streamReader.releaseLock();
                    if (this.activeReader === streamReader)
                        this.activeReader = undefined;
                }
            }
            // If we exited the loop without an explicit stop (close()), the connection failed.
            if (this.reading) {
                this.reading = false;
                // Release the port so a later connect to it doesn't find it still open.
                yield this.closePort();
                this.port = null;
                (_b = this.onDisconnect) === null || _b === void 0 ? void 0 : _b.call(this, "lost");
            }
        });
    }
    readWithTimeout(streamReader) {
        return __awaiter(this, void 0, void 0, function* () {
            let timeoutId;
            const timeoutPromise = new Promise((resolve) => {
                timeoutId = setTimeout(() => resolve({ value: undefined, done: false, timedOut: true }), this.READ_TIMEOUT_MS);
            });
            const readPromise = streamReader.read().then(({ value, done }) => ({
                value: value ? textDecoder.decode(value) : undefined,
                done,
                timedOut: false
            }));
            // Clear the timer once the race settles so resolved reads don't leave pending timeouts firing.
            return Promise.race([readPromise, timeoutPromise]).finally(() => clearTimeout(timeoutId));
        });
    }
    closePort(streamReader) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.port)
                return;
            try {
                if (streamReader) {
                    try {
                        yield streamReader.cancel();
                    }
                    catch ( /* reader may already be released */_a) { /* reader may already be released */ }
                    try {
                        streamReader.releaseLock();
                    }
                    catch ( /* lock may already be released */_b) { /* lock may already be released */ }
                }
                if (this.writer) {
                    try {
                        yield this.writer.close();
                    }
                    catch ( /* writer may already be closed */_c) { /* writer may already be closed */ }
                }
                yield this.port.close();
            }
            catch (e) {
                console.error("Error closing port:", e);
            }
        });
    }
    reopenPort() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.port)
                return false;
            try {
                yield this.port.open({ baudRate: 9600 });
                this.writer = this.port.writable.getWriter();
                return true;
            }
            catch (e) {
                console.error("Error reopening port:", e);
                return false;
            }
        });
    }
}
/**
 * Some serial devices fire the `connect` event before we call requestPort (the browser raises
 * it for devices the origin already has permission for), so we register handlers at CLUE
 * startup to catch this event and improve the connect-button UI. Not every board does this —
 * see raisesWebSerialConnect in WebSerialTransport.
 *
 * The disconnect handler records an unplug of the Web Serial port it belongs to; see the comment
 * on it.
 */
export function initWebSerialConnectionEvents(serialDevice) {
    var _a, _b;
    (_a = navigator.serial) === null || _a === void 0 ? void 0 : _a.addEventListener("connect", (e) => {
        serialDevice.updateConnectionInfo(e.timeStamp, e.type);
    });
    // The browser fires disconnect at the removed SerialPort, and it bubbles here, so `e.target` says
    // which port was unplugged; any other port's unplug leaves the device alone.
    (_b = navigator.serial) === null || _b === void 0 ? void 0 : _b.addEventListener("disconnect", (e) => {
        var _a;
        const isUnplugged = (transport) => transport instanceof WebSerialTransport && transport.lastPort === e.target;
        const active = serialDevice.activeTransport;
        if (isUnplugged(active)) {
            serialDevice.updateConnectionInfo(e.timeStamp, e.type);
            serialDevice.recordUnplug(active);
        }
        else {
            // The read loop may have given up on the port first and recorded it as lost.
            const lost = (_a = serialDevice.lastLoss) === null || _a === void 0 ? void 0 : _a.transport;
            if (isUnplugged(lost))
                serialDevice.recordUnplug(lost);
        }
    });
}
//# sourceMappingURL=data:application/json;base64,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